import { z } from 'zod';

import gasProducts from './data/gas-products.json';
import electricityProducts from './data/electricity-products.json';
import {
  EnergyType,
  ProductIDs,
  ProductType,
} from '@energy-retailer/constants';
import { createSlug } from '../utils/create-slug';

export const featureSchema = z.object({
  id: z.string(),
  icon: z.string(),
  text: z.string(),
});

export const productSchema = z
  .object({
    id: z.nativeEnum(ProductIDs),
    name: z.string(),
    enabled: z.boolean(),
    banner: z.string().optional(),
    description: z.string().optional(),
    tagline: z.string().optional(),
    features: z.array(featureSchema),
    offerDetails: z.string().optional(),
    offerTooltip: z
      .object({
        id: z.string(),
        label: z.string(),
        text: z.string(),
      })
      .optional(),
    icon: z.string(),
    productType: z.nativeEnum(ProductType),
    signupCredit: z.number().optional(),
    termsLink: z.string().optional(),
    fairUseLink: z.string().optional(),
  })
  .transform((value) => {
    const slug = createSlug.parse(value.name);

    // add a slug to the product
    return {
      ...value,
      slug,
    };
  });

export type Product = z.output<typeof productSchema>;

export const getProductsByUtility = z
  .nativeEnum(EnergyType)
  .transform((value) => {
    switch (value) {
      case EnergyType.Gas:
        return gasProducts as Product[];
      case EnergyType.Electricity:
        return electricityProducts as Product[];
    }
  })
  .transform((value) => {
    return value.filter((product) => product.enabled);
  })
  .transform((value) => {
    return productSchema.array().parse(value);
  });

export const getProductByName = z
  .object({
    name: z
      .string()
      .transform((value) => value.toLowerCase().replace(/\s/g, '-')),
    energyType: z.nativeEnum(EnergyType),
  })
  .transform((value) => {
    const products = getProductsByUtility.parse(value.energyType);

    const product = products.find((product) => product.slug === value.name);

    if (!product) {
      console.log(JSON.stringify({ products, value }, null, 2));
      throw new Error(`Product ${value.name} not found`);
    }

    return product;
  });

export const isProductEnabled = z
  .object({
    name: z.string(),
    energyType: z.nativeEnum(EnergyType),
  })
  .transform((value) => {
    const product = getProductByName.parse(value);

    return product?.enabled || false;
  });
